import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { supportAgentAPI } from '../services/api';
import {
  CASE_TYPES, CASE_STATUSES, CASE_PRIORITIES,
  statusLabel, refLabel,
} from '../constants/supportCases';
import { formatCaseDate } from '../utils/formatCaseDate';
import ContentHeader from '../components/ContentHeader';
import SupportCaseTypeBadge from '../components/SupportCaseTypeBadge';
import StatusStepper from '../components/StatusStepper';
import CaseHero from '../components/CaseHero';
import DeleteIconButton from '../components/DeleteIconButton';
import CaseAttachments from '../components/CaseAttachments';
import CaseTimeline from '../components/CaseTimeline';
import { PaperclipIcon } from '../components/icons';
import './SupportCases.css';
import './SupportConsole.css';

/**
 * Support Console — cross-tenant case-worker view (super-admin only).
 * Lives under /super-admin/support-cases. Works cases across ALL tenants.
 */
export function SupportConsoleTab() {
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [tenantOptions, setTenantOptions] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: 1, pageSize: 200 };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      if (tenantFilter) params.tenantId = tenantFilter;
      const res = await supportAgentAPI.list(params);
      const rows = res.data?.data ?? [];
      setCases(rows);
      setTotal(res.data?.total ?? 0);
      // Grow the tenant filter options as we see tenants (keeps prior entries).
      setTenantOptions((prev) => {
        const byId = new Map(prev.map((t) => [String(t.id), t]));
        rows.forEach((r) => {
          if (r.tenant_id != null && !byId.has(String(r.tenant_id))) {
            byId.set(String(r.tenant_id), { id: r.tenant_id, code: r.tenant_code, name: r.tenant_name });
          }
        });
        return Array.from(byId.values()).sort((a, b) => (a.code || '').localeCompare(b.code || ''));
      });
    } catch (err) {
      console.error('Error loading support console:', err);
      setError(err.response?.data?.error || err.message);
      setCases([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, tenantFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (caseId, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Delete this case? This permanently removes the case, its comments, and attachments.')) return;
    try {
      await supportAgentAPI.deleteCase(caseId);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete case');
    }
  };

  const showClosedOn = statusFilter === 'closed';
  const colCount = 9 + (showClosedOn ? 1 : 0);

  return (
    <div className="super-admin-tab-content">
      {error && <div className="alert alert-error"><strong>Error:</strong> {error}</div>}

      <ContentHeader
        title="Support cases"
        titleHint="Bug reports, questions, and feature requests from all tenants. Change status, assign, and reply."
      />

      <div className="support-console-filters">
        <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} aria-label="Filter by tenant">
          <option value="">All tenants</option>
          {tenantOptions.map((t) => (
            <option key={t.id} value={t.id}>{t.code}{t.name ? ` – ${t.name}` : ''}</option>
          ))}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} aria-label="Filter by type">
          <option value="">All types</option>
          {CASE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status">
          <option value="">All statuses</option>
          {CASE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <span className="support-console-count">
          {loading ? 'Loading…' : total === 1 ? '1 case' : `${total} cases`}
        </span>
      </div>

      <div className="super-admin-table">
        <table>
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Ref</th>
              <th>Subject</th>
              <th>Type</th>
              <th>Reported by</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Updated</th>
              {showClosedOn && <th>Closed on</th>}
              <th className="col-actions">Delete</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} className="no-data">Loading…</td></tr>
            ) : cases.length === 0 ? (
              <tr><td colSpan={colCount} className="no-data">No cases found.</td></tr>
            ) : (
              cases.map((c) => (
                <tr
                  key={c.id}
                  className="super-admin-row-clickable"
                  onClick={() => navigate(tenantPath(`/super-admin/support-cases/${c.id}`))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(tenantPath(`/super-admin/support-cases/${c.id}`)); } }}
                >
                  <td>{c.tenant_code}</td>
                  <td>{refLabel(c.ref)}</td>
                  <td>{c.subject}</td>
                  <td><SupportCaseTypeBadge type={c.type} /></td>
                  <td>{c.created_by_name || c.created_by_username || '—'}</td>
                  <td><span className={`support-case-status status-${c.status}`}>{statusLabel(c.status)}</span></td>
                  <td>{c.assigned_agent_name || (c.assigned_agent_username || '—')}</td>
                  <td>{formatCaseDate(c.updated_at)}</td>
                  {showClosedOn && <td>{formatCaseDate(c.closed_at)}</td>}
                  <td className="col-actions" onClick={(e) => e.stopPropagation()}>
                    <DeleteIconButton onClick={(e) => handleDelete(c.id, e)} title="Delete case" aria-label="Delete case" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Support Console — single case: change status/priority/assignment and reply.
 */
export function SupportConsoleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenantPath } = useTenantFromUrl();
  const [supportCase, setSupportCase] = useState(null);
  const [comments, setComments] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reply, setReply] = useState('');
  const [replyInternal, setReplyInternal] = useState(false);
  const [replyFile, setReplyFile] = useState(null);
  const [posting, setPosting] = useState(false);
  const [agents, setAgents] = useState([]);
  const [releases, setReleases] = useState([]);
  const [commits, setCommits] = useState([]);
  const [commitForm, setCommitForm] = useState({ sha: '', message: '', url: '' });
  const [addingCommit, setAddingCommit] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');
  const fileInputRef = useRef(null);

  const listPath = tenantPath('/super-admin/support-cases');

  const load = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const [caseRes, commentsRes, timelineRes, commitsRes] = await Promise.all([
        supportAgentAPI.getById(id),
        supportAgentAPI.listComments(id),
        supportAgentAPI.listTimeline(id),
        supportAgentAPI.listCommits(id),
      ]);
      setSupportCase(caseRes.data?.data ?? null);
      setComments(commentsRes.data?.data ?? []);
      setEvents(timelineRes.data?.data ?? []);
      setCommits(commitsRes.data?.data ?? []);
    } catch (err) {
      console.error('Error loading case:', err);
      setError(err.response?.data?.error || err.message);
      if (!silent) setSupportCase(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    supportAgentAPI.listAgents()
      .then((res) => { if (!cancelled) setAgents(res.data?.data ?? []); })
      .catch(() => { if (!cancelled) setAgents([]); });
    supportAgentAPI.listReleases()
      .then((res) => { if (!cancelled) setReleases(res.data?.data ?? []); })
      .catch(() => { if (!cancelled) setReleases([]); });
    return () => { cancelled = true; };
  }, []);

  const patchCase = async (patch) => {
    try {
      setSaving(true);
      setError(null);
      const res = await supportAgentAPI.update(id, patch);
      setSupportCase(res.data?.data ?? supportCase);
      // Refresh the timeline so the new field-change events appear immediately.
      load({ silent: true });
      return true;
    } catch (err) {
      console.error('Error updating case:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update case');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCase = async () => {
    if (!window.confirm('Delete this case? This permanently removes the case, its comments, and attachments.')) return;
    try {
      await supportAgentAPI.deleteCase(id);
      navigate(listPath);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete case');
    }
  };

  const startEditDesc = () => {
    setDescDraft(supportCase?.description || '');
    setEditingDesc(true);
  };
  const saveDesc = async () => {
    const ok = await patchCase({ description: descDraft });
    if (ok) setEditingDesc(false);
  };

  const handleAddCommit = async () => {
    const sha = commitForm.sha.trim();
    const url = commitForm.url.trim();
    const message = commitForm.message.trim();
    if (!sha && !url) { setError('A commit SHA or URL is required'); return; }
    try {
      setAddingCommit(true);
      setError(null);
      await supportAgentAPI.addCommit(id, { sha, message, url });
      setCommitForm({ sha: '', message: '', url: '' });
      const res = await supportAgentAPI.listCommits(id);
      setCommits(res.data?.data ?? []);
      // Surface the new commit-linked event in the timeline.
      load({ silent: true });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to link commit');
    } finally {
      setAddingCommit(false);
    }
  };

  const handleDeleteCommit = async (commitId) => {
    if (!window.confirm('Unlink this commit from the case?')) return;
    try {
      await supportAgentAPI.deleteCommit(id, commitId);
      setCommits((prev) => prev.filter((c) => c.id !== commitId));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to unlink commit');
    }
  };

  const handlePostReply = async () => {
    const body = reply.trim();
    if (!body || posting) return;
    try {
      setPosting(true);
      setError(null);
      const res = await supportAgentAPI.addComment(id, { body, is_internal: replyInternal });
      const commentId = res.data?.data?.id;
      if (replyFile && commentId) {
        await supportAgentAPI.uploadAttachment(id, replyFile, commentId);
      }
      setReply('');
      setReplyInternal(false);
      setReplyFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load({ silent: true });
    } catch (err) {
      console.error('Error posting reply:', err);
      setError(err.response?.data?.error || err.message || 'Failed to post reply');
    } finally {
      setPosting(false);
    }
  };

  if (loading) return <div className="super-admin-tab-content"><p>Loading case…</p></div>;
  if (!supportCase) {
    return (
      <div className="super-admin-tab-content">
        <Link to={listPath} className="support-case-back-link">← Back to cases</Link>
        {error && <div className="alert alert-error"><strong>Error:</strong> {error}</div>}
      </div>
    );
  }

  const assigneeOptions = [...agents];
  if (supportCase.assigned_agent_user_id
      && !agents.some((a) => String(a.id) === String(supportCase.assigned_agent_user_id))) {
    assigneeOptions.push({
      id: supportCase.assigned_agent_user_id,
      name: supportCase.assigned_agent_name || supportCase.assigned_agent_username || `User #${supportCase.assigned_agent_user_id}`,
    });
  }

  return (
    <div className="super-admin-tab-content support-console-detail">
      <Link to={listPath} className="support-case-back-link">← Back to cases</Link>
      <CaseHero supportCase={supportCase} showTenant />

      {error && <div className="alert alert-error"><strong>Error:</strong> {error}</div>}

      <StatusStepper
        status={supportCase.status}
        editable
        disabled={saving}
        onChange={(v) => patchCase({ status: v })}
      />

      {/* Control panel */}
      <div className="support-console-controls">
        <label>
          <span>Type</span>
          <select
            value={supportCase.type}
            disabled={saving}
            onChange={(e) => patchCase({ type: e.target.value })}
          >
            {CASE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <label>
          <span>Priority</span>
          <select
            value={supportCase.priority || ''}
            disabled={saving}
            onChange={(e) => patchCase({ priority: e.target.value })}
          >
            <option value="">— (unset)</option>
            {CASE_PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label className="support-console-assign">
          <span>Assignment</span>
          <select
            value={supportCase.assigned_agent_user_id || ''}
            disabled={saving}
            onChange={(e) => patchCase({ assigned_agent_user_id: e.target.value || null })}
          >
            <option value="">Unassigned</option>
            {assigneeOptions.map((a) => (
              <option key={a.id} value={a.id}>{a.name || a.username}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Fixed in</span>
          <select
            value={supportCase.fixed_in_release_id || ''}
            disabled={saving}
            onChange={(e) => patchCase({ fixed_in_release_id: e.target.value || null })}
          >
            <option value="">—</option>
            {releases.map((r) => <option key={r.id} value={r.id}>{r.version}</option>)}
          </select>
        </label>
      </div>

      <div className="support-console-desc">
        <div className="support-console-desc-head">
          <span className="support-console-desc-label">Description</span>
        </div>
        {editingDesc ? (
          <div className="support-console-desc-edit-box">
            <textarea
              value={descDraft}
              rows={5}
              autoFocus
              placeholder="Describe the issue or request…"
              onChange={(e) => setDescDraft(e.target.value)}
            />
            <div className="support-console-desc-actions">
              <button type="button" className="btn-save" onClick={saveDesc} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="support-console-desc-cancel" onClick={() => setEditingDesc(false)} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        ) : supportCase.description?.trim() ? (
          <div
            className="support-case-description support-console-desc-clickable"
            role="button"
            tabIndex={0}
            title="Click to edit"
            onClick={startEditDesc}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); startEditDesc(); } }}
          >
            {supportCase.description.split('\n').map((line, i) => <p key={i}>{line || ' '}</p>)}
          </div>
        ) : (
          <div
            className="support-console-desc-empty support-console-desc-clickable"
            role="button"
            tabIndex={0}
            title="Click to add a description"
            onClick={startEditDesc}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); startEditDesc(); } }}
          >
            Click to add a description…
          </div>
        )}
      </div>

      <CaseAttachments caseId={id} api={supportAgentAPI} canUpload />

      {/* Linked commits (Development) */}
      <div className="support-console-commits">
        <h2 className="support-case-thread-title">Linked commits</h2>
        <p className="support-console-commits-hint">
          Attach the commit(s) or PR that fixed this case so we can trace what change resolved it.
        </p>
        {commits.length > 0 && (
          <ul className="support-console-commit-list">
            {commits.map((c) => {
              const shortSha = c.sha ? c.sha.slice(0, 10) : null;
              const label = c.message || shortSha || c.url;
              return (
                <li key={c.id} className="support-console-commit">
                  <div className="support-console-commit-main">
                    {shortSha && <code className="support-console-commit-sha">{shortSha}</code>}
                    {c.url ? (
                      <a href={c.url} target="_blank" rel="noopener noreferrer" className="support-console-commit-msg">{label}</a>
                    ) : (
                      <span className="support-console-commit-msg">{label}</span>
                    )}
                  </div>
                  <div className="support-console-commit-meta">
                    <span>{c.linked_by_name || c.linked_by_username || 'Unknown'} · {formatCaseDate(c.created_at)}</span>
                    <DeleteIconButton onClick={() => handleDeleteCommit(c.id)} title="Unlink commit" aria-label="Unlink commit" />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="support-console-commit-form">
          <input
            type="text"
            placeholder="Commit SHA (e.g. 8f0b8ea)"
            value={commitForm.sha}
            disabled={addingCommit}
            onChange={(e) => setCommitForm((f) => ({ ...f, sha: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Message / summary (optional)"
            value={commitForm.message}
            disabled={addingCommit}
            onChange={(e) => setCommitForm((f) => ({ ...f, message: e.target.value }))}
          />
          <input
            type="text"
            placeholder="Commit / PR URL (optional)"
            value={commitForm.url}
            disabled={addingCommit}
            onChange={(e) => setCommitForm((f) => ({ ...f, url: e.target.value }))}
          />
          <button type="button" className="btn-save" onClick={handleAddCommit} disabled={addingCommit}>
            {addingCommit ? 'Linking…' : 'Link commit'}
          </button>
        </div>
      </div>

      <h2 className="support-case-thread-title">Timeline</h2>
      <CaseTimeline
        caseId={id}
        comments={comments}
        events={events}
        fetchBlob={supportAgentAPI.attachmentBlob}
        showInternal={true}
      />

      <div className="support-case-add-comment">
        <textarea
          value={reply}
          rows={4}
          placeholder={replyInternal ? 'Internal note (not shown to the reporter)…' : 'Reply to the reporter…'}
          onChange={(e) => setReply(e.target.value)}
        />
        <div className="support-case-add-comment-actions">
          <label className="support-console-internal-toggle">
            <input type="checkbox" checked={replyInternal} onChange={(e) => setReplyInternal(e.target.checked)} />
            Internal note
          </label>
          <label className="support-case-attach-link">
            <PaperclipIcon size={15} /> {replyFile ? replyFile.name : 'Attach file'}
            <input ref={fileInputRef} type="file" hidden disabled={posting}
              onChange={(e) => setReplyFile(e.target.files?.[0] || null)} />
          </label>
          <button
            type="button"
            className="btn-save"
            onClick={handlePostReply}
            disabled={reply.trim() === '' || posting}
          >
            {posting ? 'Posting…' : replyInternal ? 'Add internal note' : 'Reply'}
          </button>
        </div>
      </div>

      <div className="support-case-danger">
        <button type="button" className="support-case-delete" onClick={handleDeleteCase}>Delete case</button>
      </div>
    </div>
  );
}
